/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Client,
  CombinedError,
  fetchExchange,
  subscriptionExchange,
} from '@urql/core';
import { SubscribePayload, createClient as createWSClient } from 'graphql-ws';
import { getSdk } from './generated/graphql.urql';
import {
  createSessionAuthExchange,
  createSocketSessionHooks,
  sessionService,
} from './session';
// re-enable when we're working to get gql caching back up and running
// import schema from './generated/graphql.schema.urql.json' assert { type: 'json' };
// need to spend more time to figure out if this is helpful until we have full graphql subscriptions wired up
// import { refocusExchange } from '@urql/exchange-refocus';

const wsClient = createWSClient({
  url: import.meta.env.FE_GQL_WSS_ENDPOINT,
  // A socket that dies takes the cross-tab logout signal with it: the
  // subscription only reports an auth-shaped error when it RECONNECTS as
  // `anonymous` (PSTUDIO-4022).
  on: createSocketSessionHooks(sessionService),
  connectionParams: () => {
    return {
      credentials: 'include',
      headers: {
        // while not fully wired up right now, this header is how we can look up / enforce low-level permissions in Hasura
        'x-hasura-user-id': sessionService.identityId() ?? '',
      },
    };
  },
});

// Default gqlClient with all endpoints configured.
const gqlClient = (customHeaders?: HeadersInit) =>
  new Client({
    url: import.meta.env.FE_GQL_ENDPOINT,
    requestPolicy: 'cache-and-network',
    exchanges: [
      // Turns an expired Kratos session into an actual auth error: the gateway
      // answers 200 with the `anonymous` role instead of failing (PSTUDIO-4022).
      createSessionAuthExchange(sessionService),
      // @todo: need to re-enable and work on getting this to work with new Apollo GQL resolvers in Grizzly codebase
      // cacheExchange({
      //   // schema: schema,
      //   keys: new Proxy(
      //     {
      //       // UserRole: () => null,
      //       GetLoginInfo: () => null,
      //     },
      //     {
      //       get(_target) {
      //         // @todo: spend more time with this urql caching functionality to see if we can get this more fully typed
      //         // eslint-disable-next-line @typescript-eslint/no-explicit-any
      //         // return (data: any) => data[`${prop.split('_')[1]}_id`];
      //         return (data: any) => data['id'];
      //       },
      //     }
      //   ),
      //   resolvers: {
      //     // Google's API for manipulating this data programmatically isn't great: https://developers.google.com/people/image-sizing
      //     User: {
      //       picture: (user: User) => transformProfilePicture(user?.picture), // bump up the user profile image resolution
      //     },
      //   },
      // }),
      // refocusExchange(),
      fetchExchange,
      subscriptionExchange({
        forwardSubscription(operation) {
          return {
            subscribe: (sink) => {
              const dispose = wsClient.subscribe(
                operation as SubscribePayload,
                sink
              );
              return {
                unsubscribe: dispose,
              };
            },
          };
        },
      }),
    ],
    fetchOptions: () => {
      const headers: HeadersInit = {};
      const identityId = sessionService.identityId();

      if (identityId) {
        headers['x-hasura-user-id'] = identityId;
      }

      return {
        credentials: 'include',
        headers: {
          ...headers,
          ...customHeaders,
        },
      };
    },
  });

// Export all generated types
export * from './generated/graphql.urql';

export const urqlGql = getSdk(gqlClient());

const getUrqlGql = (customHeaders?: HeadersInit) =>
  getSdk(gqlClient(customHeaders));

export const proStudioUrqlGql = getUrqlGql({
  UserID: 'UserID',
  UserEmail: 'UserEmail',
  UserFirstName: 'UserFirstName',
  UserLastName: 'UserLastName',
});

export const initProStudioUrqlGql = ({
  userId: UserID,
  userEmail: UserEmail,
  userFirstName: UserFirstName,
  userLastName: UserLastName,
}: {
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
}) =>
  getUrqlGql({
    UserID,
    UserEmail,
    UserFirstName,
    UserLastName,
  });

export type UrqlGqlType = ReturnType<typeof getUrqlGql>;

export { CombinedError };
